/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import ifImg from 'assets/images/if.png'
import ifElseImg from 'assets/images/ifElse.png'
import ifElseIfImg from 'assets/images/ifElseIf.png'
import switchImg from 'assets/images/switch.png'

export const _11 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Операторы ветвеления</h2>
    <p className={styles.description}>
      Логические операторы не могут самостоятельно управлять потоком выполнения программы, для этого используются
      ветвления. Все они устроены по одному принципу - входные данные приводятся к булю (
      <code className={styles.code}>true</code> или <code className={styles.code}>false</code>) и, в зависимости от
      результата этого значения, поток программы направляется в ту или иную ветку.
    </p>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>2 - Инструкция if</h2>
      <img className={styles.img_without_shadow} src={ifImg} alt="ifImg" />
      <p className={styles.description}>
        Входные данные, которые приводятся к булевому типу называются <b>условием</b>. Условие помещают за оператором{' '}
        <code className={styles.code}>if</code> в круглых скобках. Если условие приводится к{' '}
        <code className={styles.code}>true</code>, то выполняется код в фигурных скобках (ветка).
      </p>
      <CopyBlock
        text={`let cost = 0;
const subscription = 'pro';

if (subscription === 'pro') {
  cost = 100;
}

console.log(cost); // 100
`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Если условие приводится к <code className={styles.code}>false</code>, код в фигурных скобках будет пропущен.
      </p>
      <CopyBlock
        text={`let cost = 0;
const subscription = 'free';

if (subscription === 'pro') {
  cost = 100;
}

console.log(cost); // 0
`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.1 - Инструкция if...else</h3>
        <img className={styles.img_without_shadow} src={ifElseImg} alt="ifElseImg" />
        <p className={styles.description}>
          Расширяет синтаксис оператора <code className={styles.code}>if</code> тем, что в случае если условие
          приводится к <code className={styles.code}>false</code>, выполнится код в фигурных скобках после оператора{' '}
          <code className={styles.code}>else</code>.
        </p>
        <CopyBlock
          text={`let cost;
const subscription = 'free';

if (subscription === 'pro') {
  cost = 100;
} else {
  cost = 0;
}

console.log(cost); // 0
`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          При <code className={styles.code}>true</code>, оператор <code className={styles.code}>else</code> и связанный
          с ним программный блок, игнорируются.
        </p>
        <CopyBlock
          text={`let cost;
const subscription = 'pro';

if (subscription === 'pro') {
  cost = 100;
} else {
  cost = 0;
}

console.log(cost); // 100
`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.2 - Инструкция else...if</h3>
        <img className={styles.img_without_shadow} src={ifElseIfImg} alt="ifElseIfImg" />
        <p className={styles.description}>
          Расширяет синтаксис оператора <code className={styles.code}>if...else</code> тем, что после{' '}
          <code className={styles.code}>else</code> снова добавляется оператор <code className={styles.code}>if</code>.
          На первый взгляд код из множества подобных вложений кажется сложным. На самом деле, все ответвления - это
          результат <code className={styles.code}>false</code> на все предыдущие вопросы.
        </p>
        <p className={styles.description}>
          При первом же <code className={styles.code}>true</code> проверки прекратятся и выполнится только один
          сценарий, соответствующий этому <code className={styles.code}>true</code>. Поэтому подобную запись следует
          читать как: ищу первое совпадение условия, игнорирую все остальное.
        </p>
        <CopyBlock
          text={`let cost;
const subscription = 'premium';

if (subscription === 'free') {
  cost = 0;
} else if (subscription === 'pro') {
  cost = 100;
} else if (subscription === 'premium') {
  cost = 500;
} else {
  console.log('Invalid subscription type');
}

console.log(cost); // 500`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>3 - Тернарный оператор</h2>
      <p className={styles.description}>
        Есть конструкция, похожая на <code className={styles.code}>if...else</code>, с упрощенным синтаксисом,
        называемая тернарный оператор (ternary - тройной).
      </p>
      <CopyBlock
        text={`{условие} ? {выражение если условие правдиво} : {выражение если условие не правдиво}`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Такая конструкция работает следующим образом:</p>
      <ul className={styles.list}>
        <li className={styles.list__item}>Вычисляется условие</li>
        <li className={styles.list__item}>
          Если условие <code className={styles.code}>истинно (true)</code>, вычисляется выражение после{' '}
          <code className={styles.code}>?</code>, в противном случае значение после{' '}
          <code className={styles.code}>:</code>
        </li>
        <li className={styles.list__item}>Результат вычисленного выражения возвращается</li>
      </ul>
      <CopyBlock
        text={`let type;
const age = 20;

if (age >= 18) {
  type = 'adult';
} else {
  type = 'child';
}`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Перепишем пример используя тернарный оператор.</p>
      <CopyBlock
        text={`const age = 20;
const type = age >= 18 ? 'adult' : 'child';`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Запишем операцию поиска большего числа.</p>
      <CopyBlock
        text={`const num1 = 5;
const num2 = 10;
let biggerNumber;

if (num1 > num2) {
  biggerNumber = num1;
} else {
  biggerNumber = num2;
}

console.log(biggerNumber); // 10`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Код работает верно, получаем большее число из двух, но это решение кажется слишком громоздким, учитывая,
        насколько проста проблема. Что делать? Используем тернарный оператор.
      </p>
      <CopyBlock
        text={`const num1 = 5;
const num2 = 10;
const biggerNumber = num1 > num2 ? num1 : num2;

console.log(biggerNumber); // 10`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.notificationError}>
        Тернарный оператор должен использоваться в простых операциях присваивания или возврата. Его использование для
        описания сложных ветвлений — плохая практика (антипаттерн).
      </p>
    </section>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>4 - Инструкция switch</h2>
      <p className={styles.description}>
        В некоторых случаях сложности чтения логических конструкций можно избежать, используя оператор ветвления{' '}
        <code className={styles.code}>switch</code>. Синтаксис этого оператора разбивает условие на общую часть{' '}
        <code className={styles.code}>switch</code> и множество отдельных случаев{' '}
        <code className={styles.code}>case</code>. Т.е. применимость этого оператора ограничена только задачами с одним
        общим вопросом и множеством вариантов ответов.
      </p>
      <img className={styles.img_without_shadow} src={switchImg} alt="switchImg" />
      <p className={styles.description}>
        Значение выражения - строка или число, которое сравнивается со всеми значениями
        <code className={styles.code}>case</code>. Если совпадения не произошло, управление передается{' '}
        <code className={styles.code}>default</code>. Оператор
        <code className={styles.code}>break</code> в завершении каждого блока <code className={styles.code}>case</code>{' '}
        ставят чтобы прервать дальнейшие проверки и сразу перейти к коду за инструкцией{' '}
        <code className={styles.code}>switch</code>.
      </p>
      <CopyBlock
        text={`let cost;
const subscription = 'premium';

switch (subscription) {
  case 'free':
    cost = 0;
    break;

  case 'pro':
    cost = 100;
    break;

  case 'premium':
    cost = 500;
    break;

  default:
    console.log('Invalid subscription type');
}

console.log(cost); // 500`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Если оператор <code className={styles.code}>break</code> будет отсутствовать, то после того как выполнится
        какое-то условие <code className={styles.code}>case</code>, все последующие за ним блоки кода будут выполняться
        один за другим, что может привести к нежелательным последствиям при неверном применении.
      </p>
    </section>
  </section>
))
